import { FC, useState } from 'react';
import { InterventionReportModel } from '../../models/interventionReportModel';
import {
  InterventionReportProps,
  InterventionReportsKebabOption,
} from './interventionReports.type';
import { InterventionReportsTableHeader } from './interventionReportsTableHeader';
import { InterventionReportsTableFilter } from './interventionReportsTableFilter';
import Table from '../../component/table/table';
import { getInterventionReportEditPermissions } from '../../utils/interventionReportEditPermissions';
import { useUserDetailContext } from '../../service/context/userDetailContext';
import {
  getInterventionReportsKebabOptions,
  getInterventionReportsTableHeader,
  getInterventionReportTitle,
  formatSelectedReportRefs,
} from './interventionReportsHelper';
import InterventionReportForm from '../../component/interventionReportForm/interventionReportForm';
import {
  useDownloadInterventionReport,
  useInterventionReports,
} from '../../hooks/useInterventionReports';
import {
  getConsecutiveSweeps,
  downloadPdfFromBase64,
} from '../../utils/helper';
import { InterventionReportDetails } from './interventionReportDetails';
import UserVerificationModal from '../../component/userVerificationModal/userVerificationModal';
import { Trans } from 'react-i18next';
import { ConfirmationModalV2 } from '../../component/confirmationModalV2/confirmationModalV2';
import SelectPlayerModal from '../../component/selectPlayerModal/selectPlayerModal';
import { toastV2 } from '../../component/toastV2/toastV2';
import { downloadReportsAsZip } from '../../utils/downloadReportsAsZip';
import { reportService } from '../../service/instances';
import { BulkDownloadResult } from '../../utils/helper.type';

export const InterventionReports: FC<InterventionReportProps> = () => {
  const [activeRowIndex, setActiveRowIndex] = useState<number>(-1);
  const {
    InterventionReports: interventionReports,
    handleClearFilters,
    handleReportInterventionClose,
    handleKebabActionFromKebabMenu,
    handleOnChangeSearch,
    handleDateRangeChange,
    loading,
    isTablet,
    organizationContextLoading,
    selectedVenueBranch,
    filters,
    openFilterModal,
    toggleFilterModal,
    handleApplyFilters,
    activeFiltersExist,
    openCreateIntervention,
    isReviewAndEditMode,
    selectedRows,
    handleSelectionChange,
    handleRowClick,
    interventionReportForCreate,
    t,
    selectedInterventionReport,
    handleReviewAndSignClick,
    closeInterventionReportDetails,
    setSelectedRows,
    showDeleteConfirmation,
    setShowDeleteConfirmation,
    deleting,
    handleDeleteInterventionReports,
    handleDeleteClick,
    showUserVerification,
    handleOnVerify,
    setShowUserVerification,
    handleReportInterventionClick,
    setShowSelectPlayerModal,
    showSelectPlayerModal,
    handleOnProceed,
    verifiedUser,
  } = useInterventionReports();

  const { userDetail } = useUserDetailContext();
  const {
    downloadInterventionReportLoading,
    bulkDownloadLoading,
    setBulkDownloadLoading,
    setDownloadInterventionReportLoading,
  } = useDownloadInterventionReport();

  const downloadInterventionReportsAsZip: (
    reports: InterventionReportModel[],
  ) => Promise<void> = async (
    reports: InterventionReportModel[],
  ): Promise<void> => {
    if (bulkDownloadLoading || !reports.length) return;
    setBulkDownloadLoading(true);
    try {
      const result: BulkDownloadResult = await downloadReportsAsZip(reports);

      if (result.failedReportRefNos.length > 0) {
        const failedDisplay: string =
          result.failedReportRefNos.length <= 3
            ? result.failedReportRefNos.join(', ')
            : `${result.failedReportRefNos.slice(0, 3).join(', ')} and ${result.failedReportRefNos.length - 3} more`;

        toastV2.warning(
          `Downloaded ${result.successCount} of ${reports.length} reports. Failed: ${failedDisplay}`,
        );
      }
    } catch (error: unknown) {
      console.error('[useDownloadInterventionReport][bulk][ERROR]', error);
      toastV2.error('Failed to download intervention reports');
    } finally {
      setBulkDownloadLoading(false);
    }
  };

  const downloadInterventionReport: (
    reportId: string,
    reportRefNo?: string,
  ) => Promise<void> = async (
    reportId: string,
    reportRefNo?: string,
  ): Promise<void> => {
    if (downloadInterventionReportLoading) return;
    setDownloadInterventionReportLoading(true);
    try {
      const pdfBase64: string =
        await reportService.downloadInterventionReport(reportId);

      if (!pdfBase64) {
        throw new Error('No PDF data returned from API');
      }
      downloadPdfFromBase64(
        pdfBase64,
        `Intervention Report_${reportRefNo || reportId}`,
      );
    } catch (error) {
      console.error(`[useDownloadInterventionReport][ERROR]`, error);
      toastV2.error('Failed to download intervention report');
    } finally {
      setDownloadInterventionReportLoading(false);
    }
  };

  return (
    <div className="header-content">
      <div
        className="table-content pt-6 flex flex-col overflow-hidden"
        style={{ boxShadow: '0px 0px 25px #2F41A51F' }}
      >
        {showUserVerification && (
          <UserVerificationModal
            show={showUserVerification}
            onToggle={() =>
              setShowUserVerification((currentValue: boolean) => !currentValue)
            }
            onVerified={handleOnVerify}
            scope={'intc'}
          />
        )}
        {showSelectPlayerModal && (
          <SelectPlayerModal
            onClose={() => {
              setShowSelectPlayerModal(false);
            }}
            onProceed={handleOnProceed}
          />
        )}
        <InterventionReportsTableHeader
          reportLabel={
            selectedRows.length > 1
              ? t('common.multi_report')
              : t('sweeps.report_one')
          }
          searchQuery={filters.searchQuery}
          handleOnChangeSearch={handleOnChangeSearch}
          selectedCount={selectedRows.length}
          selectedDateRange={filters.selectedDateRange}
          disableSettingMenu={true}
          handleReportInterventionClick={handleReportInterventionClick}
          handleDateRangeChange={handleDateRangeChange}
          handleClose={() => setSelectedRows([])}
          handleDelete={handleDeleteClick}
          handleDownload={() => {
            downloadInterventionReportsAsZip(selectedRows);
          }}
          showDownloadLoading={bulkDownloadLoading}
        >
          <InterventionReportsTableFilter
            isOpenUserModal={openFilterModal}
            selectedBranchIDs={
              selectedVenueBranch ? [selectedVenueBranch._id] : []
            }
            hasActiveFilters={activeFiltersExist}
            handleClearFilters={handleClearFilters}
            handleApplyFilters={handleApplyFilters}
            toggleFilterModal={toggleFilterModal}
            currentDateRange={filters.selectedDateRange}
            currentFilters={filters}
            reports={interventionReports}
          />
        </InterventionReportsTableHeader>
        {/* Table    */}
        <div className="flex-grow">
          <Table
            onSelectionChange={handleSelectionChange}
            tableHeader={getInterventionReportsTableHeader(isTablet, t)}
            selectedRows={selectedRows}
            tableData={interventionReports}
            loading={loading || organizationContextLoading}
            onViewDetail={handleRowClick}
            onKebabSelect={(
              action: InterventionReportsKebabOption,
              row: InterventionReportModel,
              rowIndex: number,
            ) => {
              setActiveRowIndex(rowIndex);
              if (action === InterventionReportsKebabOption.DOWNLOAD) {
                if (row.originalReportId) {
                  downloadInterventionReport(
                    row.originalReportId,
                    row.reportRefNo,
                  );
                }
                return;
              }
              handleKebabActionFromKebabMenu(action, row, rowIndex);
            }}
            loadingMenu={
              downloadInterventionReportLoading
                ? {
                    rowIndex: activeRowIndex,
                    action: InterventionReportsKebabOption.DOWNLOAD,
                  }
                : { rowIndex: -1, action: '' }
            }
            getKebabMenuOptions={(
              row: any,
              loadingMenu: any,
              rowIndex: number,
            ) => {
              const perms = getInterventionReportEditPermissions(
                row,
                userDetail?.type,
                userDetail?.id,
              );
              return getInterventionReportsKebabOptions(
                row,
                loadingMenu,
                rowIndex,
                userDetail?.type,
                perms.canEdit,
              );
            }}
            onShouldCloseKebabMenu={(action) =>
              action !== InterventionReportsKebabOption.DOWNLOAD
            }
          />
        </div>
        {openCreateIntervention && (
          <InterventionReportForm
            title={getInterventionReportTitle(
              interventionReportForCreate?.reportRefNo,
              isReviewAndEditMode,
              t,
            )}
            data={interventionReportForCreate?.raw ?? null}
            player={null}
            consecutiveSweeps={getConsecutiveSweeps(
              interventionReportForCreate?.raw ?? null,
            )}
            harmCount={null}
            interventionOffered={null}
            interventionRequested={null}
            isReviewAndEditMode={isReviewAndEditMode}
            verifiedUser={verifiedUser}
            onBack={handleReportInterventionClose}
            linkedHarmReportIds={
              interventionReportForCreate?.harmReport?.map(
                (linkedReport: { id: string; refNo: string }) =>
                  linkedReport.id,
              ) ?? []
            }
            linkedHarmRefNos={
              interventionReportForCreate?.harmReport?.map(
                (linkedReport: { id: string; refNo: string }) =>
                  linkedReport.refNo,
              ) ?? []
            }
            editPermissions={
              interventionReportForCreate
                ? getInterventionReportEditPermissions(
                    interventionReportForCreate.raw,
                    userDetail?.type,
                    userDetail?.id,
                  )
                : undefined
            }
          />
        )}
        {selectedInterventionReport && (
          <InterventionReportDetails
            interventionReport={selectedInterventionReport}
            onClose={closeInterventionReportDetails}
            onReviewAndSign={handleReviewAndSignClick}
            downloadLoading={downloadInterventionReportLoading}
            onKebabAction={(action) => {
              if (action === InterventionReportsKebabOption.DOWNLOAD) {
                if (selectedInterventionReport?.originalReportId) {
                  downloadInterventionReport(
                    selectedInterventionReport.originalReportId,
                    selectedInterventionReport.reportRefNo,
                  );
                }
                return;
              }
              closeInterventionReportDetails();
              handleKebabActionFromKebabMenu(
                action as any,
                selectedInterventionReport,
                0,
              );
            }}
          />
        )}
        {showDeleteConfirmation && (
          <ConfirmationModalV2
            note="interventionReports.deletionNote"
            isOpen={showDeleteConfirmation}
            showConfirmButton
            title="common.deleteReport"
            onClose={() => setShowDeleteConfirmation(false)}
            message={
              <Trans
                i18nKey="interventionReports.deleteConfirmation"
                values={{
                  value: formatSelectedReportRefs(
                    selectedRows.map((selectedRow) => selectedRow.raw),
                  ),
                  report:
                    selectedRows.length === 1
                      ? t('interventionReports.singleReport')
                      : t('interventionReports.multipleReports'),
                }}
                components={{ bold: <strong /> }}
              />
            }
            isLoading={deleting}
            onConfirm={handleDeleteInterventionReports}
          />
        )}
      </div>
    </div>
  );
};
